import { useTranslation } from 'react-i18next';
import { useContext, useState, useRef, useEffect } from 'react';
import {
  IconButton,
  Tag,
  Button,
  ComboBox,
  InputText,
  Spinner,
  FilterDropdown,
  InputSelect,
} from '@teo/components';
import {
  CrossFilled,
  DuoToneCourseFilled,
  Search,
} from '@teo/components/icons';
import { axios } from '@/axios';
import { config } from '@/config';
import { langData } from '@/mocks/landData';
import { AuthContext } from '@/components/Contexts/AuthContext';
import CompetencyProfilesModal from './CompetencyProfilesModal';
import { parseTypeCourse, useMobile, parseFilter, newUid } from '@/util';
import Hint from '../Hints/Hints';
import HintOverlay from '../Hints/HintOverlay';
import { HintsContext } from '@/components/Contexts/HintsContext';
import { useGet } from '@/query/fetchHooks';
import { useParams } from 'react-router-dom';
import { PaginationAnything } from '@/components/DataTable/PaginationAnything';
import { LangData } from '@/models/langData.interface';
import { languagesShort } from '@/constants/languages';

const TypeCourse = ({
  setIsOpenTypeModal,
  typeCourse,
  setGoToCreate,
  setPublicationId,
  publicationId,
  setLoadeNewPublication,
}: any) => {
  const { t } = useTranslation();
  const contentRef = useRef(null);
  const { organisationId } = useParams();
  const { auth } = useContext(AuthContext);
  const authUser: any = auth?.user;
  const authLang: any = auth?.user.lang;
  const authOrg: any = auth?.user?.activeOrganisationId;
  const [loaded, setLoaded] = useState(false);
  const [loadedList, setLoadedList] = useState(true);
  const [isOpenCompetency, setIsOpenCompetency] = useState(false);
  const [selectCompetency, setSelectCompetency] = useState<any[]>([]);
  const [contentRange, setContentRange] = useState(0);

  const isMobile = useMobile();
  const { hints, setHints } = useContext(HintsContext);
  const [filter, setFilter] = useState('');
  const [selectTemplate, setSelectTemplate] = useState<any>(null);
  const [name, setName] = useState('');
  const [refresh, setRefresh] = useState<string>(newUid(20));
  const [newLangData, setNewLangData] = useState<any>([]);
  const [selectLang, setSelectLang] = useState<any>(null);

  useEffect(() => {
    const sortLangData = langData
      ?.map((el) => ({
        ...el,
        name: t(`langs.${el?.value}`),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    setSelectLang(
      sortLangData?.find((option: LangData) => option?.value === authLang)
    );
    setNewLangData(sortLangData);
  }, [langData]);

  const handleStart = async () => {
    setLoadeNewPublication && setLoadeNewPublication(true);
    const id = selectTemplate?.id;
    // const id = templates?.[authLang]?.[template];
    const newId = await axios.post(`${config.backend}/publish/copy/${id}`);
    const publicationId = newId?.data?.id;
    setPublicationId(newId?.data?.id);

    await Promise.allSettled([
      axios.post(`${config.backend}/membercoursecontents`, {
        role: 90,
        coursecontentId: publicationId,
        organisationId: authOrg,
      }),
      axios.put(`${config.backend}/coursecontent/${publicationId}`, {
        authorId: auth?.user?.id,
        publicationStatus: 'concept',
        publicationCourseType: parseTypeCourse(typeCourse),
        name: name ? name : newId?.data?.name,
        template: 'practice',
        lang:
          selectLang?.value != 'null' ? selectLang?.value : newId?.data?.lang,
      }),
      selectCompetency?.map((el: any, index: any) => addCompetency(el, index)),
    ]).then(() => {
      setGoToCreate(true);
    });
  };

  const addCompetency = async (el: any, index: any) => {
    const data = await axios.post(
      `${config.backend}/coursecontentprofessions`,
      {
        coursecontentId: publicationId,
        professionId: el?.key,
      }
    );
    if (selectCompetency?.length - 1 === index) {
      data?.data?.professionId && setLoaded(true);
    }
  };

  // Before the JSX (inside your component)
  const langParam = () => {
    if (typeCourse !== 'instructions') {
      if (languagesShort.includes(selectLang?.value)) {
        return selectLang?.value;
      } else if (selectLang?.value !== 'null') {
        return `${selectLang?.value},OR,lang=null`;
      }
    } else if (selectLang?.value !== 'null') {
      return `${selectLang?.value},OR,lang=null`;
    }
    return undefined;
  };

  const publicationCourseTypeParam = () => {
    const baseType = parseTypeCourse(typeCourse);
    if (typeCourse !== 'instructions') {
      if (languagesShort.includes(selectLang?.value)) {
        return `${baseType},OR,publicationCourseType=general,AND,lang=${selectLang?.value}`;
      } else if (selectLang?.value !== 'null') {
        return `${baseType},OR,publicationCourseType=general,AND,lang=${authLang},OR,publicationCourseType=general,AND,lang=null`;
      } else {
        return `${baseType},OR,publicationCourseType=general,AND,lang=${authLang}`;
      }
    } else {
      return `${baseType},OR,publicationCourseType=general,AND,lang=null`;
    }
  };

  return (
    <div
      ref={contentRef}
      className="fixed top-0 left-0 z-[100] flex h-screen w-screen flex-col overflow-y-auto bg-white"
    >
      <div className="flex min-h-16 flex-col justify-start border-b border-grey-02 py-2 pr-2 sm:flex-row sm:items-center">
        <div
          data-testid="clouseBtn"
          className="flex min-h-16 items-center justify-start"
        >
          <IconButton
            className="my-auto mx-3 sm:mx-6"
            Icon={CrossFilled}
            variant="ghost"
            onClick={() => {
              setIsOpenTypeModal(false);
            }}
          />
          <div className="flex items-center">
            <div className="mr-8">
              <DuoToneCourseFilled className="w-6" />
            </div>
            <p className="mr-8 font-medium">
              {t(`pages.create_publication.name_${typeCourse}`)}
            </p>
            <Tag title={t('pages.create_publication.draft')} size="md" />
          </div>
        </div>
      </div>

      <div className="m-auto flex w-full max-w-3xl flex-col gap-4 p-3 py-5 md:gap-8">
        <div className="">
          <h3 className="mb-4 text-xl font-normal">
            {t(
              `pages.create_publication.give_title_${parseTypeCourse(
                typeCourse
              )}`
            )}
          </h3>

          <InputText
            value={name}
            onChange={(ev: any) => setName(ev.target.value)}
            placeholder={t(
              `pages.create_edit_course.type_${parseTypeCourse(
                typeCourse
              )}.title_course_placeholder`
            )}
          />
        </div>
        <div className="">
          <h3 className="mb-4 text-xl font-normal">
            {t(`pages.create_publication.template_lang_course`)}
          </h3>
          <div id="templateLang">
            <InputSelect<LangData>
              onChange={(lang) => {
                setSelectLang(lang);
                setFilter('');
                setSelectTemplate(null);
                setRefresh(newUid(20));
              }}
              value={
                selectLang
                  ? selectLang
                  : newLangData?.find(
                      (option: LangData) => option?.value === authLang
                    )
              }
              options={[
                {
                  name: t('pages.create_library.all'),
                  value: 'null',
                  id: 1234567,
                  flag: false,
                },
                ...newLangData,
              ]}
            >
              {(options) => (
                <span>
                  {options?.value != 'null' ? options?.name : options?.name}
                </span>
              )}
            </InputSelect>
          </div>
        </div>
        <div className="combo-box flex flex w-full flex-col">
          <h3 className="mb-4 text-xl font-normal">
            {t(`pages.create_publication.title_maodal_${typeCourse}`)}
          </h3>
          {(contentRange > 6 || filter?.length > 0) && (
            <InputText
              value={filter}
              onChange={(ev) => {
                setLoadedList(true);
                setFilter(ev.target.value);
                setRefresh(newUid(20));
              }}
              Icon={Search}
              placeholder={t('pages.create_publication.search_template')}
              className="mb-4"
            />
          )}
          <div className="relative flex min-h-[150px] flex-col">
            {loadedList && (
              <div className="absolute inset-0 z-[11] flex bg-white">
                <div className="m-auto">
                  <Spinner
                    ariaLabel="Loading spinner"
                    className="!h-16 !w-16 border-grey-08"
                  />
                </div>
              </div>
            )}
            <div className="min-h-[405px]">
              <PaginationAnything
                filter={filter}
                key={refresh}
                setContentRange={setContentRange}
                className={`-ml-2 -mr-2 flex min-h-[405px] flex-wrap`}
                fetch={useGet.bind(null, '/myowedpublicationtemplates', {
                  lang: langParam(),
                  userId: authUser?.id,
                  organisationId: `${organisationId},OR,organisationId=null`,
                  publicationCourseType: publicationCourseTypeParam(),
                  publicationStatus: 'published-template',
                  onSuccess: (result: any) => {
                    setLoadedList(false);
                  },
                })}
                pageSize={6}
                defaultSort={[
                  { id: 'template', desc: false },
                  {
                    id: 'publicationCourseType',
                    desc:
                      typeCourse === 'vacancies' ||
                      typeCourse === 'instructions'
                        ? true
                        : false,
                  },
                  { id: 'updatedAt', desc: true },
                ]}
                render={(template: any) => (
                  <div className="basis-1/2 p-2 sm:basis-1/3">
                    <div
                      key={template?.id}
                      className={`group flex-1 cursor-pointer overflow-hidden rounded-lg  border border-grey-02 px-4 py-3 ${
                        template?.id == selectTemplate?.id &&
                        'border-secondary-04'
                      } relative h-[185px]`}
                      onClick={() =>
                        setSelectTemplate({
                          id: template?.id,
                          label: template?.name,
                        })
                      }
                    >
                      {selectLang?.value == 'null' && (
                        <div className="absolute top-0 right-2 hidden group-hover:block">
                          <img
                            key={template.id}
                            src={`/images/flags/${
                              template.lang ? template.lang : 'en'
                            }.svg`}
                            alt={`${template.lang} flag`}
                            className="
                          mt-2
                          ml-2
                          h-[25px]
                          w-[25px] rounded-[50%]
                          bg-tint-red
                          object-cover
                          "
                          />
                        </div>
                      )}
                      <h4 className="text-base">{template?.name}</h4>
                      {template?.description && (
                        <p className="mt-2 text-sm text-grey-07 line-clamp-5">
                          {template?.description}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              />
            </div>
            {!(contentRange > 6) && <div className="h-[68px]"></div>}
          </div>
        </div>
        <div className="flex">
          <Button
            type="button"
            variant="outline"
            className={`relative mr-auto ${
              hints?.id == `hint_31` &&
              'active-hint-create-page active-hint-parent active-hint-main-menu !z-[100]'
            }`}
            onClick={() => setIsOpenCompetency(true)}
          >
            <Hint
              key={`hint_31`}
              id={'hint_31'}
              leftDots={'-left-2'}
              title={t('hint.title_31')}
              order={31}
              overlaySecond={true}
              style={{
                left: '-5px',
                bottom: 'calc(100% + 15px)',
              }}
            />
            {t('pages.create_publication.btn_competency_profiles')}
          </Button>
          <Button
            type="button"
            onClick={handleStart}
            disabled={!selectTemplate}
          >
            {t('pages.create_publication.btn_start_editing')}
          </Button>
        </div>
      </div>
      {isOpenCompetency && (
        <CompetencyProfilesModal
          setIsOpenCompetency={setIsOpenCompetency}
          setSelectCompetency={setSelectCompetency}
          selectCompetency={selectCompetency}
        />
      )}

      {hints?.active === 2 && !isMobile && (
        <HintOverlay setHints={setHints} hints={hints} block={contentRef} />
      )}
    </div>
  );
};

export default TypeCourse;
